import { useMutation, useQuery } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, Modal, ModalCloseButton } from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import { OrderItem } from 'contracts/OrderItems';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import { Trash } from 'lucide-react';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import * as S from './styles';
import { useProduct } from 'hooks/api/general/useProduct';

export interface Ref {
  openModal: () => void;
}

interface Props {
  item: OrderItem;
  onUpdate?: (item: any) => void;
}

interface IEditItemBatchModal
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

const EditItemBatchModal: IEditItemBatchModal = forwardRef((props, ref) => {
  const { item, onUpdate } = props;
  const formRef = useRef<FormHandles>(null);
  const [isOpen, setIsOpen] = useState(false);

  //criar lista de items com item inicial vindo das props
  const [items, setItems] = useState<any[]>([]);

  const { updateOrderItems } = useOrder();

  const { findProduct } = useProduct();

  const { mutate: itemMutation, isPending: isUpdating } = useMutation({
    mutationFn: (payload: any) => updateOrderItems(payload),
    onSuccess: (data: any) => {
      toast('Lote do item alterado com sucesso');
      onModalClose();
      onUpdate && onUpdate(data);
    },
    onError: (error: any) => {
      toast('Erro ao editar item', {
        description: error.response.data.message,
      });
    },
  });

  const { data: product } = useQuery({
    queryKey: ['findProduct', item.code],
    queryFn: () => findProduct(item.code),
    refetchOnWindowFocus: false,
    staleTime: 3e5,
    enabled: !!item.code,
  });

  const validateVolume = (quantity: number, insertedVolume?: string) => {
    const factor = product?.factor;
    if (!factor) return insertedVolume;

    let correctVolume = quantity / factor;

    if (!insertedVolume) return correctVolume;

    if (Number(insertedVolume) < correctVolume) return correctVolume;

    return insertedVolume;
  };

  const updateItem = useCallback(
    (id: string, key: string, value: any) => {
      const updatedItems = items.map((item) => {
        if (item.id === id) {
          return { ...item, [key]: value };
        }
        return item;
      });
      setItems(updatedItems);
    },
    [items]
  );

  const addLote = useCallback(
    (e: any) => {
      e.preventDefault();
      setItems([
        ...items,
        {
          ...item,
          id: crypto.randomUUID(),
          quantity: 0,
          batch: '',
        },
      ]);
    },
    [items, item]
  );

  const handleSubmit = useCallback((): void => {
    //validar quantidades, o total dos items não pode ser maior que o total do item original
    const total = items.reduce((acc, item) => acc + Number(item.quantity), 0);

    if (total !== item.quantity) {
      toast(
        'A soma das quantidades dos lotes não pode ser diferente da quantidade original do item'
      );
      return;
    }

    //valida se todos os items tem lote e quantidade
    const invalidItems = items.filter(
      (item) => !item.batch || !item.quantity || item.quantity <= 0
    );
    if (invalidItems.length > 0) {
      toast('Todos os lotes devem ter o número lote e quantidade preenchidos');
      return;
    }

    const hasInvalidVolume = items.some(
      (orderItem: OrderItem) =>
        product?.factor &&
        orderItem.quantity / product.factor < orderItem.volume
    );

    if (hasInvalidVolume) {
      toast('Exitem lotes com volume menor que o permitido');
      return;
    }

    itemMutation(items.map(({ meta, ...item }) => item));
  }, [items, item, itemMutation]);

  const onModalClose = useCallback((): void => {
    setIsOpen(false);
    formRef.current?.setErrors({});
    formRef.current?.reset();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: (): void => {
        setIsOpen(true);
      },
    }),
    []
  );

  useEffect(() => {
    setItems([
      {
        ...item,
        ...(product?.factor && { volume: validateVolume(item.quantity) }),
      },
    ]);
  }, [item, product]);

  return (
    <Modal isOpen={isOpen} onClickOutside={onModalClose}>
      <S.ModalContent style={{ maxWidth: '720px' }}>
        <S.ModalHeader>
          <h4>Editar lote</h4>
          <ModalCloseButton onClick={onModalClose} />
        </S.ModalHeader>
        <S.ModalBody>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <S.FormRow>
              <Input
                name="description"
                label="Descrição do item"
                value={item.description}
                readOnly
                disabled
              />
            </S.FormRow>
            {items.map((batch, index) => (
              <S.FormRow key={index}>
                <Input
                  name="batch"
                  label="Lote do item"
                  value={batch.batch}
                  onChange={(e) =>
                    updateItem(batch.id, 'batch', e.target.value)
                  }
                />
                <Input
                  name="quantidade"
                  label="Quantidade"
                  value={batch.quantity}
                  onChange={(e) =>
                    updateItem(batch.id, 'quantity', e.target.value)
                  }
                />
                <Input
                  name="volume"
                  label="Volume"
                  type="number"
                  value={batch.volume}
                  min={validateVolume(batch.quantity)}
                  onBlur={(e) => {
                    const validatedVolume = validateVolume(
                      batch.quantity,
                      e.target.value
                    );

                    updateItem(batch.id, 'volume', validatedVolume);
                  }}
                  onChange={({ target }) => {
                    updateItem(batch.id, 'volume', target.value);
                  }}
                />
                <S.TrashButton
                  type="button"
                  disabled={batch.id === item.id}
                  mood="danger"
                  onClick={(e) =>
                    setItems(items.filter((i) => i.id !== batch.id))
                  }
                >
                  <Trash />
                </S.TrashButton>
              </S.FormRow>
            ))}
            <S.Button type="button" onClick={(e) => addLote(e)}>
              Dividir lote
            </S.Button>

            <S.FormActions>
              <S.Button type="button" mood="light" onClick={onModalClose}>
                {MODAL_DISMISS_ACTION}
              </S.Button>
              <S.Button type="submit">
                {isUpdating ? <S.ActivityIndicator /> : 'Salvar'}
              </S.Button>
            </S.FormActions>
          </Form>
        </S.ModalBody>
      </S.ModalContent>
    </Modal>
  );
});

export default EditItemBatchModal;
